import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchUpcomingEvents } from '~/services/dashboard';

export interface IUpcomingEvent {
  id: number;
  name: string;
  event_date: string | null;
  event_end_date: string | null;
  event_time_text: string;
  formatted_display_date: string;
  location: string;
  is_virtual: boolean;
  campaign_id: number;
  patient_name: string | null;
  campaign_ntaf_id: string;
}

const CACHE_KEY = 'upcoming_events_cache';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

// Parse a YYYY-MM-DD string as a local date to avoid UTC-shift bugs where
// `new Date("2025-10-15")` becomes UTC midnight and renders as the prior day
// in any timezone west of UTC.
const parseLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const formatShortDate = (dateString: string | null, endDateString: string | null): string => {
  if (!dateString) return 'No date';

  const startDate = parseLocalDate(dateString);
  const month = startDate.toLocaleDateString('en-US', { month: 'short' });
  const day = startDate.getDate();
  const year = startDate.getFullYear();

  // If there's an end date, show date range
  if (endDateString) {
    const endDate = parseLocalDate(endDateString);
    const endMonth = endDate.toLocaleDateString('en-US', { month: 'short' });
    const endDay = endDate.getDate();
    const endYear = endDate.getFullYear();
    
    // Same month and year: "Oct 15-20, 2025"
    if (startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear()) {
      return `${month} ${day}-${endDay}, ${year}`;
    }
    // Same year: "Oct 15 - Nov 20, 2025"
    if (startDate.getFullYear() === endDate.getFullYear()) {
      return `${month} ${day} - ${endMonth} ${endDay}, ${year}`;
    }
    // Different years: "Dec 25, 2024 - Jan 5, 2025"
    return `${month} ${day}, ${year} - ${endMonth} ${endDay}, ${endYear}`;
  }
  
  // Single date: "Oct 15, 2025"
  return `${month} ${day}, ${year}`;
};

const UpcomingEvents: React.FC = () => {
  const [events, setEvents] = useState<IUpcomingEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadEvents = async () => {
      // Check cache first
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < CACHE_DURATION && Array.isArray(data)) {
            setEvents(data);
            setLoading(false);
            return;
          }
        }
      } catch (e) {
        console.error('Error reading events cache:', e);
      }

      // Fetch fresh data
      try {
        const data = await fetchUpcomingEvents();
        const eventsArray = Array.isArray(data) ? data : [];
        setEvents(eventsArray);
        // Cache the result only if valid
        if (eventsArray.length > 0) {
          localStorage.setItem(CACHE_KEY, JSON.stringify({
            data: eventsArray,
            timestamp: Date.now(),
          }));
        }
      } catch (error) {
        console.error('Error fetching upcoming events:', error);
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
  }, []);

  return (
    <div className='h-full rounded-sm border border-stroke bg-white p-4 shadow-default dark:border-strokedark dark:bg-boxdark md:p-6 xl:p-7.5'>
      <div className='mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <h2 className='text-xl font-semibold text-black dark:text-white'>
          Upcoming Events {events && events.length > 0 && `(${events.length})`}
        </h2>
      </div>

      {loading ? (
        <div className='flex items-center justify-center py-8'>
          <div className='h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent'></div>
        </div>
      ) : !events || events.length === 0 ? (
        <div className='py-8 text-center text-secondary'>
          No upcoming events
        </div>
      ) : (
        <div className='flex flex-col gap-2'>
          <div className='grid grid-cols-12 gap-2 py-2'>
            <div className='col-span-5'>
              <p className='text-sm text-secondary'>Event Name</p>
            </div>
            <div className='col-span-4'>
              <p className='text-sm text-secondary'>Patient</p>
            </div>
            <div className='col-span-3'>
              <p className='text-sm text-secondary'>Date</p>
            </div>
          </div>
          {events.map((event) => (
            <div key={event.id} className='relative z-1 grid grid-cols-12 gap-2 py-2'>
              <div className='col-span-5'>
                <Link 
                  to={`/campaigns/${event.campaign_id}`}
                  className='text-primary hover:underline'
                >
                  {event.name}
                </Link>
                {event.is_virtual && (
                  <span className='ml-2 text-xs text-secondary'>(Virtual)</span>
                )}
              </div>
              <div className='col-span-4'>
                <p className='text-sm'>{event.patient_name || event.campaign_ntaf_id}</p>
              </div>
              <div className='col-span-3'>
                <p className='text-sm'>{formatShortDate(event.event_date, event.event_end_date)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UpcomingEvents;
